import React, { useEffect, useState } from "react";
import Navbar from "../NavBar";
import Footer from "../Footer";
import Seo from "../Seo";
import { breadcrumbJsonLd } from "../../seoSchema";
import "../../styles/Customize.css";
import { initializeApp } from "firebase/app";
import { getFirestore, doc, getDoc } from "firebase/firestore";
import { cmsHeroBackgroundStyle, resolveFirestoreImage, useCmsHeroImageReady } from "../../firestoreMedia";

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIRESTORE_API_KEY,
    authDomain: "wishuponaplayhouse.firebaseapp.com",
    projectId: "wishuponaplayhouse",
    storageBucket: "wishuponaplayhouse.firebasestorage.app",
    messagingSenderId: "513377368862",
    appId: "1:513377368862:web:eadda2a0b7bb141ffa670f",
    measurementId: "G-NZ116LS0Q1",
};

const app = initializeApp(firebaseConfig);
const firestore = getFirestore(app);

const HIGHLIGHTS = [
    {
        label: "Any Style You Can Imagine",
        body: "Victorian, Firehouse, Swiss Chalet, Fort, Cabin — or a miniature of your own home. If you can dream it, we can build it.",
    },
    {
        label: "Built On-Site, Just For You",
        body: "Every custom playhouse is constructed in your backyard with the same quality craftsmanship as our signature styles.",
    },
    {
        label: "Designed Around Your Child",
        body: "Our design team works with you to choose every detail — colors, doors, windows, and personal touches that make it truly theirs.",
    },
];

function Customize() {
    const [title, setTitle] = useState("");
    const [paragraph, setParagraph] = useState("");
    const [heroImage, setHeroImage] = useState(null);
    const [pageReady, setPageReady] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const docRef = doc(firestore, "Pages", "CustomizePage");
                const docSnap = await getDoc(docRef);
                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setTitle(data.title);
                    setParagraph(data.description);
                    if (data.heroImage) setHeroImage(data.heroImage);
                }
            } catch (error) {
                console.error("Error fetching customize content: ", error);
            } finally {
                setPageReady(true);
            }
        };
        fetchData();
    }, []);

    const czHeroUrl = pageReady ? resolveFirestoreImage(heroImage, "/images/mainstory.webp") : "";
    const czHeroReady = useCmsHeroImageReady(czHeroUrl, pageReady);

    return (
        <div className="cz-page">
            <Seo
                title="Custom Playhouse Design | Dream Playhouse Builder in Denver, CO"
                description="Design your dream custom playhouse with Denver's premier children's playhouse builder. Choose colors, features, and add-ons — built on-site anywhere in Colorado."
                path="/playhouses/customize"
                jsonLd={breadcrumbJsonLd([
                    { name: "Home", path: "/" },
                    { name: "Playhouses", path: "/playhouses" },
                    { name: "Customize", path: "/playhouses/customize" },
                ])}
                jsonLdId="customize-breadcrumb"
            />
            <Navbar />
            <>
                    {/* ── HERO ── */}
                    <div className="sub-hero">
                        <div
                            className={`sub-hero-bg${pageReady ? "" : " cms-hero-pending-fill"}`}
                            style={{
                                ...cmsHeroBackgroundStyle(!pageReady, heroImage, "/images/mainstory.webp"),
                                opacity: pageReady ? (czHeroReady ? 1 : 0) : 1,
                                transition: "opacity 0.55s cubic-bezier(0.22, 1, 0.36, 1)",
                            }}
                        />
                        <div className="sub-hero-overlay" />
                        <div className="sub-hero-content">
                            <p className="sub-hero-eyebrow">Make It Yours</p>
                            <h1 className="sub-hero-title">{title || "Custom Playhouses"}</h1>
                            <a href="/contact" className="sub-hero-btn">
                                <span>Start My Design</span>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </a>
                        </div>
                    </div>

                    {/* ── HIGHLIGHTS ── */}
                    <div className="cz-highlights">
                        {HIGHLIGHTS.map((h) => (
                            <div key={h.label} className="cz-highlight-card">
                                <h3 className="cz-highlight-title">{h.label}</h3>
                                <p className="cz-highlight-body">{h.body}</p>
                            </div>
                        ))}
                    </div>

                    {/* ── DESCRIPTION (Firebase) ── */}
                    <div className="cz-description-wrap">
                        <p className="cz-eyebrow">The Details</p>
                        <p className="cz-description">{paragraph}</p>
                    </div>

                    {/* ── CTA BAND ── */}
                    <div className="cz-cta-band">
                        <div className="cz-cta-inner">
                            <h2 className="cz-cta-heading">Ready to Build Something One-of-a-Kind?</h2>
                            <p className="cz-cta-sub">Tell us your vision and we'll bring it to life — no two custom playhouses are ever the same.</p>
                            <a href="/contact" className="cz-cta-btn">
                                <span>Request a Custom Quote</span>
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                                </svg>
                            </a>
                        </div>
                    </div>
            </>

            <Footer />
        </div>
    );
}

export default Customize;
